import { useState, useEffect } from "react";
import "./payrollSettings.css";
import "../../companyDetails.css";
import T from "utils/constants/T";
import { get } from "lodash";
import {
  StyledBodyTypography,
  StyledTitleTypography,
} from "../CompanyTab/CompanyInfo";
import MainCard from "components/MainCard";
import { Button, Grid, MenuItem, TextField, CircularProgress, Stack, Box, Typography } from "@mui/material";
import CashDCard from "components/common/CashDCard";
import { useDispatch } from "react-redux";
import { fetchReckonDeductionItems, updateDeductionMappings } from "store/reducers/reckon";
import { fetchCompanyDetails } from "store/reducers/companyTabDetails";
import toast from "utils/ToastNotistack";

interface ViewPayrollSettingsReckonProps {
  reckonAuthDetails: any;
  companyName: string;
  systemName: string;
  role: string;
  selectedCompanyId: string;
  handleSyncReckon: () => void;
}

const ViewPayrollSettingsReckon = ({
  reckonAuthDetails,
  systemName = "",
  role = "",
  selectedCompanyId,
  handleSyncReckon,
}: ViewPayrollSettingsReckonProps) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deductionItems, setDeductionItems] = useState<any[]>([]);

  const currentCapitalId = get(reckonAuthDetails, "deductionMappings.capital.payItemId", "");
  const currentCapitalName = get(reckonAuthDetails, "deductionMappings.capital.payItemName", "");
  const currentFeeId = get(reckonAuthDetails, "deductionMappings.fee.payItemId", "");
  const currentFeeName = get(reckonAuthDetails, "deductionMappings.fee.payItemName", "");

  const [selectedCapitalId, setSelectedCapitalId] = useState(currentCapitalId);
  const [selectedFeeId, setSelectedFeeId] = useState(currentFeeId);

  // Sync initial state when reckonAuthDetails updates
  useEffect(() => {
    setSelectedCapitalId(currentCapitalId);
    setSelectedFeeId(currentFeeId);
  }, [currentCapitalId, currentFeeId]);

  const handleEditClick = async () => {
    setIsEditing(true);
    setIsLoadingItems(true);
    try {
      const items = await dispatch((fetchReckonDeductionItems as any)(selectedCompanyId));
      setDeductionItems(items || []);
    } catch (error: any) {
      toast(error?.message || "Failed to fetch Reckon deduction categories.", { variant: "error" });
      setIsEditing(false);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleCancel = () => {
    setSelectedCapitalId(currentCapitalId);
    setSelectedFeeId(currentFeeId);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!selectedCapitalId || !selectedFeeId) {
      toast("Please select both Capital and Fee deduction items.", { variant: "warning" });
      return;
    }

    setIsSaving(true);
    try {
      const selectedCapitalItem = deductionItems.find((item) => item.id === selectedCapitalId);
      const selectedFeeItem = deductionItems.find((item) => item.id === selectedFeeId);

      const payload = {
        capital: {
          payItemId: selectedCapitalId,
          payItemName: selectedCapitalItem?.name || "",
        },
        fee: {
          payItemId: selectedFeeId,
          payItemName: selectedFeeItem?.name || "",
        },
      };

      const res = await dispatch((updateDeductionMappings as any)(selectedCompanyId, payload));
      if (res?.success || res?.type === "success") {
        toast("Deduction mappings updated successfully.", { variant: "success" });
        setIsEditing(false);
        // Refresh company details
        dispatch((fetchCompanyDetails as any)(selectedCompanyId));
      } else {
        toast(res?.message || "Failed to save deduction mappings.", { variant: "error" });
      }
    } catch (error: any) {
      toast(error?.message || "Error saving deduction mappings.", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <MainCard
      title={systemName}
      secondary={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            className="main-card-button"
            onClick={handleSyncReckon}
            disabled={isSaving}
          >
            {T.reAuth}
          </Button>
        </Stack>
      }
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <StyledBodyTypography sx={{ fontWeight: 600, mb: 0 }}>
          Payroll Deduction Mapping
        </StyledBodyTypography>
        {(role === "superadmin" || role === "employer") && !isEditing && (
          <Button
            variant="contained"
            className="main-card-button"
            onClick={handleEditClick}
          >
            {T.EDIT}
          </Button>
        )}
      </Box>

      {isEditing ? (
        <Box sx={{ p: 2 }}>
          {isLoadingItems ? (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Loading deduction categories from Reckon One...</Typography>
            </Stack>
          ) : (
            <Stack spacing={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="CashD Capital Repayment Category *"
                    value={selectedCapitalId}
                    onChange={(e) => setSelectedCapitalId(e.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select Capital Deduction Item
                    </MenuItem>
                    {deductionItems
                      .filter((item) => item.id !== selectedFeeId)
                      .map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label="CashD Transaction Fee Category *"
                    value={selectedFeeId}
                    onChange={(e) => setSelectedFeeId(e.target.value)}
                  >
                    <MenuItem value="" disabled>
                      Select Fees Deduction Item
                    </MenuItem>
                    {deductionItems
                      .filter((item) => item.id !== selectedCapitalId)
                      .map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              </Grid>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <CircularProgress size={20} color="inherit" /> : "Save"}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      ) : (
        <Stack spacing={3}>
          <CashDCard borderRadius="20px">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <StyledTitleTypography>{T.CASH_D_CAPITAL}</StyledTitleTypography>
                <StyledBodyTypography>
                  {currentCapitalName || "Not Configured"}
                </StyledBodyTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTitleTypography>{T.CASH_D_FEE}</StyledTitleTypography>
                <StyledBodyTypography>
                  {currentFeeName || "Not Configured"}
                </StyledBodyTypography>
              </Grid>
            </Grid>
          </CashDCard>
        </Stack>
      )}
    </MainCard>
  );
};

export default ViewPayrollSettingsReckon;
